import { useCallback, useMemo } from "react";
import { useFetch } from "../useFetch";
import type {
	OrderResponse,
	CreateOrderPayload,
	PaymentStatus as PaymentStatusType,
	RejectionOrderPayload,
	SendOrderPaymentReceiptPayload,
	SendOrderPaymentReceiptResponse,
	WarrantyOrderPayload,
	UpdateStatusOrderPayload,
	StatusOrder,
	PaginatedResponse,
} from "../../core/types/services";
import { PaymentStatus } from "../../core/types/services";

// ============================================
// GET ALL ORDERS (PAGINATED)
// ============================================
export function useOrders(
	page = 1,
	pageSize = 10,
	search?: string, // Nuevo
	status?: StatusOrder, // Nuevo
	date?: string, // Nuevo (YYYY-MM-DD)
	location?: string, // Nuevo
) {
	const query = useMemo(() => {
		// Construimos el objeto de parámetros dinámicamente
		const params: Record<string, string | number> = { page, pageSize };

		if (search) params.search = search;
		if (status) params.status = status;
		if (date) params.date = date;
		if (location) params.location = location;

		return params;
	}, [page, pageSize, search, status, date, location]);

	return useFetch<PaginatedResponse<OrderResponse>>({
		endpoint: "/orders",
		method: "GET",
		query,
	});
}

// ============================================
// GET ORDER BY ID
// ============================================
export function useOrder(id: number) {
	const orderFetch = useFetch<OrderResponse>({
		endpoint: `/orders/${id}`,
		method: "GET",
		auto: id > 0,
	});

	const statusFetch = useFetch<OrderResponse, UpdateStatusOrderPayload>({
		endpoint: `/orders/${id}/status`,
		method: "PATCH",
		auto: false,
	});

	const warrantyFetch = useFetch<OrderResponse, WarrantyOrderPayload>({
		endpoint: `/orders/${id}/warranty`,
		method: "PATCH",
		auto: false,
	});

	const sendReceiptFetch = useFetch<
		SendOrderPaymentReceiptResponse,
		SendOrderPaymentReceiptPayload
	>({
		endpoint: `/orders/${id}/send-payment-receipt`,
		method: "POST",
		auto: false,
	});

	const refreshOrder = () => orderFetch.execute();

	const releaseOrderPayment = async (reason = "Pago liberado") => {
		const result = await statusFetch.execute({
			body: { status: "Pagado" as StatusOrder, reason },
		});
		await refreshOrder();
		return result;
	};

	const markOrderSupport = async (reason = "Marcado para soporte") => {
		const result = await statusFetch.execute({
			body: { status: "Soporte" as StatusOrder, reason },
		});
		await refreshOrder();
		return result;
	};

	const claimOrderWarranty = async (reason = "Reclamo de garantía por cliente") => {
		const expirationDate =
			orderFetch.data?.date_expiration_guarantee || new Date().toISOString();

		await warrantyFetch.execute({
			body: {
				is_guarantee_used: true,
				date_expiration_guarantee: expirationDate,
			},
		});

		const result = await statusFetch.execute({
			body: { status: "Garantia" as StatusOrder, reason },
		});
		await refreshOrder();
		return result;
	};

	const sendOrderPaymentReceipt = async (
		payload: SendOrderPaymentReceiptPayload,
		receiptImageFile?: File,
	) => {
		return sendReceiptFetch.execute({
			body: payload,
			files: receiptImageFile ? { receipt_image: receiptImageFile } : undefined,
		});
	};

	return {
		...orderFetch,
		releaseOrderPayment,
		markOrderSupport,
		claimOrderWarranty,
		sendOrderPaymentReceipt,
		sendReceiptLoading: sendReceiptFetch.loading,
		sendReceiptError: sendReceiptFetch.error,
		actionLoading: statusFetch.loading || warrantyFetch.loading,
		actionError: statusFetch.error || warrantyFetch.error,
	};
}

// ============================================
// GET ORDERS BY STATUS
// ============================================
export function useOrdersByStatus(status: StatusOrder, page = 1, pageSize = 10) {
	const query = useMemo(() => ({ page, pageSize }), [page, pageSize]);

	return useFetch<PaginatedResponse<OrderResponse>>({
		endpoint: `/orders/status/${status}`,
		method: "GET",
		query,
	});
}

// ============================================
// GET ORDERS BY SUPPORT OR WARRANTY STATUS
// ============================================
export function useSupportWarrantyOrders(page = 1, pageSize = 10) {
	const query = useMemo(() => ({ page, pageSize }), [page, pageSize]);

	return useFetch<PaginatedResponse<OrderResponse>>({
		endpoint: "/orders/support-warranty",
		method: "GET",
		query,
	});
}

// ============================================
// GET ORDERS BY PAYMENT STATUS
// ============================================
export function useOrdersByPaymentStatus(
	status: PaymentStatusType = PaymentStatus.PENDING,
	page = 1,
	pageSize = 10,
) {
	const query = useMemo(() => ({ status, page, pageSize }), [status, page, pageSize]);

	return useFetch<PaginatedResponse<OrderResponse>>({
		endpoint: "/orders/payment-status",
		method: "GET",
		query,
	});
}

// ============================================
// GET ORDERS BY PROVIDER
// ============================================
export function useOrdersByProvider(providerId: number, page = 1, pageSize = 10) {
	const query = useMemo(() => ({ page, pageSize }), [page, pageSize]);

	return useFetch<PaginatedResponse<OrderResponse>>({
		endpoint: `/orders/provider/${providerId}`,
		method: "GET",
		query,
		auto: providerId > 0,
	});
}

// ============================================
// GET ORDERS BY PROVIDER (SAFE REFRESH)
// ============================================
export function useProviderOrders(providerId: number | null, page = 1, pageSize = 10) {
	const ordersFetch = useOrdersByProvider(providerId ?? 0, page, pageSize);

	const refreshProviderOrders = useCallback(() => {
		if (!(typeof providerId === "number" && providerId > 0)) {
			return Promise.resolve(null);
		}

		return ordersFetch.execute({ endpoint: `/orders/provider/${providerId}` });
	}, [providerId, ordersFetch]);

	return {
		...ordersFetch,
		refreshProviderOrders,
	};
}

// ============================================
// GET ORDERS BY CLIENT
// ============================================
export function useOrdersByClient(clientId: number, page = 1, pageSize = 10) {
	const query = useMemo(() => ({ page, pageSize }), [page, pageSize]);

	return useFetch<PaginatedResponse<OrderResponse>>({
		endpoint: `/orders/client/${clientId}`,
		method: "GET",
		query,
		auto: clientId > 0,
	});
}

// ============================================
// CREATE ORDER
// ============================================
export function useCreateOrder() {
	const fetch = useFetch<OrderResponse, CreateOrderPayload>({
		endpoint: "/orders",
		method: "POST",
		auto: false,
	});

	const create = (data: CreateOrderPayload) => {
		return fetch.execute({ body: data });
	};

	return { ...fetch, create };
}

// ============================================
// UPDATE ORDER STATUS
// ============================================
export function useUpdateOrderStatus(id: number) {
	const fetch = useFetch<OrderResponse, UpdateStatusOrderPayload>({
		endpoint: `/orders/${id}/status`,
		method: "PATCH",
		auto: false,
	});

	const updateStatus = (status: StatusOrder, reason: string) => {
		return fetch.execute({ body: { status, reason } });
	};

	return { ...fetch, updateStatus };
}

// ============================================
// CANCEL / REJECT ORDER (Set Reason)
// ============================================
export function useSetOrderRejectionReason(id: number) {
	const fetch = useFetch<OrderResponse, RejectionOrderPayload>({
		endpoint: `/orders/${id}/rejection-reason`,
		method: "PATCH",
		auto: false,
	});

	const setReason = (reason: string) => {
		// Mapeamos 'reason' al campo del DTO 'cancelation_reason'
		return fetch.execute({ body: { cancelation_reason: reason } });
	};

	return { ...fetch, setReason };
}

// ============================================
// SET WARRANTY INFO
// ============================================
export function useSetOrderWarranty(id: number) {
	// Nota: El DTO original espera { warrantyInfo: ... } o campos planos?
	// Basado en tu hook anterior, enviabas un objeto anidado `warrantyInfo`.
	// Si el backend espera los campos planos, elimina el wrapper { warrantyInfo: ... }.

	// Asumiremos estructura plana basada en el DTO de class-validator que mostraste:
	const fetch = useFetch<OrderResponse, WarrantyOrderPayload>({
		endpoint: `/orders/${id}/warranty`,
		method: "PATCH",
		auto: false,
	});

	const setWarranty = (data: WarrantyOrderPayload) => {
		return fetch.execute({ body: data });
	};

	return { ...fetch, setWarranty };
}
